import { useState, useMemo, useRef, useEffect } from 'react';
import { usePlayerCard } from '../context/PlayerCardContext';
import { PLAYERS, UNIQUE_PLAYERS, POS_COLORS } from '../data/players';
import { PLAYER_HISTORY } from '../data/playerHistory';
import { findPlayerHist } from '../utils/projlabProjection';
import { WEEKLY_STATS } from '../data/weeklyStats';
import { TIER_MAPS, getTierVal, getTierForRank } from '../data/tiers';
import { ADP_DATA, getEffectivePosRank, parseRankNum } from '../data/adpData';
import { COACHING_HISTORY } from '../data/coachingHistory';
import { getActual2025PPG } from '../engine/projections';
import { projectPoints, projectGames, resolveProjection } from '../utils/projectionSource';
import { BYE_WEEKS_2026 } from '../data/scheduleData';
import { computeStats } from './RangeOfOutcomes';
import StarToggle from './StarToggle';

// Strip common name suffixes for fuzzy matching
const stripSuffix = (name) => name.replace(/\s+(Jr\.?|Sr\.?|II|III|IV|V)$/i, '').trim();

// Extract display last name, skipping suffixes
const getLastName = (name) => stripSuffix(name).split(' ').pop() || name;

// Find player with suffix fallback
function findPlayer(name) {
  return PLAYERS.find(p => p.name === name) || PLAYERS.find(p => stripSuffix(p.name) === stripSuffix(name));
}

// PPR pts/game benchmark for the 12th-ranked player at each position
const POS_BENCHMARKS = { QB: 17, RB: 13, WR: 14, TE: 9 };
const SEASONS = ['2025', '2024', '2023'];

// Tier badge color gradient by tier number
const TIER_COLORS = {
  1: '#f59e0b', 2: '#f59e0b', 3: '#22c55e', 4: '#22c55e',
  5: '#3b82f6', 6: '#3b82f6', 7: '#94a3b8', 8: '#64748b', 9: '#475569',
};

// Rostered counts for 10-team standard (starters + flex share + bench share)
const REPLACEMENT_COUNTS = { QB: 17, RB: 35, WR: 33, TE: 10, DEF: 10, K: 10 };

// Compute replacement-level projection per position (memoize-friendly)
function computeReplacementLevels(scoring) {
  const levels = {};
  for (const pos of ['QB', 'RB', 'WR', 'TE']) {
    // Same layer as the numerator in `vorp` below and as usePricing's own
    // replacementLevels — a VORP whose two halves come from different projection
    // engines is meaningless, and this is the fallback path that computes both.
    const pool = UNIQUE_PLAYERS.filter(p => p.pos === pos)
      .map(p => projectPoints(p, scoring))
      .sort((a, b) => b - a);
    const idx = Math.min(REPLACEMENT_COUNTS[pos] || 10, pool.length) - 1;
    levels[pos] = idx >= 0 ? pool[idx] : 0;
  }
  return levels;
}

/* ── Single Player Column ──────────────────────────────────────────────── */

/**
 * "My guys" — star/unstar, on the card itself.
 *
 * 🔴 SHOWN ON EVERY SURFACE, not only when the card was opened from Draft
 * Plan. A star is a personal marker on a PLAYER, not a Draft Plan mechanic, and
 * gating it on `source` would make one card grow and lose a control depending
 * on where you happened to click it. The payoff only shows up in Draft Plan;
 * marking a guy while you are looking at him in Team Builder is the point.
 *
 * 🔴 Rendered only when a toggle has actually been registered (`canStar`). A
 * provider with no plan state behind it — a test harness, any standalone mount
 * — gets no control rather than a dead one.
 */
function CardStarToggle({ player, isCompare }) {
  const { isStarred, toggleStar, canStar } = usePlayerCard();
  if (!canStar || !player) return null;
  // The composite key every sheet in this app uses. A bare name silently
  // misses — two players can share one and the price map is keyed this way too.
  const key = `${player.name}-${player.pos}`;
  const starred = isStarred(key);
  return (
    <StarToggle
      starred={starred}
      name={player.name}
      size={isCompare ? 20 : 24}
      testId="card-star-toggle"
      onToggle={() => toggleStar(key)}
    />
  );
}

function PlayerColumn({ name, scoring, replacementLevels, dynamicPrices, budget, activeLeagueName, isCompare, onRemove, compareSearch, hideRangeOfOutcomes = false }) {
  const [selectedSeason, setSelectedSeason] = useState(SEASONS[0]); // most recent (2025)
  const [showVorpExplain, setShowVorpExplain] = useState(false);
  const player = useMemo(() => findPlayer(name), [name]);

  // Raw (unrounded) season points over a replacement starter. Displayed as a
  // per-game edge (÷17), so we keep the full-precision value here.
  // MUST come from projectionSource, not a local re-derivation: `replacementLevels`
  // is computed by usePricing from projectPoints, so scoring the numerator any
  // other way subtracts two different projection engines from each other. That
  // became a live unit mismatch the moment usePricing moved to the projection layer.
  const vorp = useMemo(() => {
    if (!player || !scoring) return 0;
    const proj = projectPoints(player, scoring);
    const replacement = replacementLevels[player.pos] || 0;
    return proj - replacement;
  }, [player, scoring, replacementLevels]);

  // All-seasons weekly points, used for the career summary line (typical/range/games).
  const careerWeeklyData = useMemo(() => {
    const gsis = player?.gsis_id;
    if (!gsis) return [];
    const pw = WEEKLY_STATS[gsis];
    if (!pw) return [];
    const out = [];
    for (const s of SEASONS) {
      const weeks = pw[s];
      if (!weeks) continue;
      weeks.forEach((entry, i) => {
        out.push({ week: i + 1, season: s, points: weekPts(entry, scoring, player?.pos) });
      });
    }
    return out;
  }, [player?.gsis_id, scoring]);

  if (!player) {
    return <p style={{ color: '#94a3b8', padding: 24 }}>Player not found</p>;
  }

  const posColor = POS_COLORS[player.pos] || POS_COLORS.BN;
  const adpEntry = ADP_DATA.find(a => a.name === player.name);
  const adpDisplay = adpEntry ? adpEntry.adp : '—';
  const effectiveRank = getEffectivePosRank(player.name, player.posRank) || player.posRank;
  const rankNum = parseRankNum(effectiveRank);
  const tierObj = getTierForRank(player.pos, rankNum);
  const tierNum = tierObj ? tierObj.tier : null;
  const tierLabel = tierObj ? tierObj.label : '—';
  const tierColor = TIER_COLORS[tierNum] || '#64748b';
  // League-dynamic price (same as Team Builder), keyed `${name}-${pos}`; fall
  // back to the tier value only when the map has no entry.
  const price = dynamicPrices?.get(`${player.name}-${player.pos}`) ?? getTierVal(player, budget ?? 200);
  // 2025 ACTUAL scoring-aware PPG (null for players with no 2025 stats, e.g.
  // rookies). NOT a projection — labeled "'25 PPG" so it can't be mistaken for one.
  // Derive from the SAME 2025 weekly series the chart & compare legend use (mean of
  // weekPts over games played) so the tile, the legend's "/G", and the on-chart
  // average line always agree. getActual2025PPG scores SEASON TOTALS via
  // calculateProjection — which includes a fumble penalty the per-week data can't
  // represent (no `fum` field on any week) — so it read ~0.2 low vs the chart.
  // Fall back to it only when a player has no weekly 2025 data (no chart to match).
  const actualPPG = (() => {
    const weeks2025 = player.gsis_id && WEEKLY_STATS[player.gsis_id] ? WEEKLY_STATS[player.gsis_id]['2025'] : null;
    if (weeks2025) {
      const pts = weeks2025.map(w => weekPts(w, scoring, player.pos)).filter(v => v != null);
      if (pts.length) return Math.round((pts.reduce((s, v) => s + v, 0) / pts.length) * 10) / 10;
    }
    return getActual2025PPG(player.name, player.pos, scoring);
  })();
  // 2026 PROJECTED PPG — Mock1's own number, the forward-looking twin of the
  // tile beside it. The divisor is projectGames, never 17: a season total is
  // normalised to the games the projection was scored over, and dividing by a
  // constant instead runs about 12% low on every skill player.
  //
  // resolveProjection is the null test rather than `pts === 0`, because a
  // resolved projection that happens to score zero is a real zero and should
  // read 0.0, not as a missing number.
  const projectedPPG = (() => {
    if (!resolveProjection(player)) return null;
    const g = projectGames(player);
    if (!g) return null;
    return Math.round((projectPoints(player, scoring) / g) * 10) / 10;
  })();
  // Per-game VORP: weekly points edge over a replacement starter (season / 17).
  // Availability is already baked into the season projection, so ÷17 is correct.
  const vorpPerGame = vorp / 17;
  const vpgSigned = `${vorpPerGame >= 0 ? '+' : ''}${vorpPerGame.toFixed(1)}`;
  // Thresholds re-tuned for the per-game scale (NOT the old total-VORP bands ÷17).
  const vpgColor = vorpPerGame >= 6 ? '#22c55e' : vorpPerGame >= 3 ? '#e2e8f0' : vorpPerGame >= 1.5 ? '#f59e0b' : '#64748b';
  // On-card scoring context — the numbers above (PPG/PRICE/VORP) are scoring-dependent.
  const scoringNote = (() => {
    const r = scoring?.receptions ?? 0.5;
    const ppr = r === 1 ? 'Full PPR' : r === 0.5 ? 'Half-PPR' : r === 0 ? 'Non-PPR' : `${r} PPR`;
    const label = `${ppr}, ${scoring?.passTD ?? 6}pt Pass TD`;
    return activeLeagueName
      ? `Scored with your ${activeLeagueName} settings · ${label}`
      : `Default scoring · ${label} — load a league to customize`;
  })();

  // Coaching data (hidden in compare mode)
  const coachingTeam = COACHING_HISTORY[player.team];
  const coaching2026 = coachingTeam ? coachingTeam.find(c => c.year === 2026) : null;

  const showWeeklyViz = !isCompare && player.gsis_id && ['QB', 'RB', 'WR', 'TE'].includes(player.pos);
  // Career summary values (typical = median PPG, range = min–max, games = n) — the
  // same computeStats the old density curve used, now shown as one plain text line.
  const careerStats = showWeeklyViz ? computeStats(careerWeeklyData) : null;

  const abbreviatedName = (() => {
    const stripped = stripSuffix(player.name);
    const parts = stripped.split(/\s+/).filter(Boolean);
    if (parts.length < 2) return parts[0] || player.name;
    return `${parts[0][0]}. ${parts[parts.length - 1]}`;
  })();

  return (
    <div className="pc-player-column" style={{ padding: isCompare ? '20px 16px' : 24 }}>
      {isCompare && onRemove && (
        <button
          onClick={onRemove}
          title="Remove player"
          aria-label="Remove player"
          style={{
            position: 'absolute', top: 4, right: 4,
            width: 16, height: 16, padding: 0,
            background: 'transparent', border: 'none',
            color: '#64748b', fontSize: 14, lineHeight: 1,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >&times;</button>
      )}
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isCompare ? 10 : 16, marginBottom: isCompare ? 12 : 20, paddingRight: isCompare ? 0 : 36 }}>
        <img
          src={`https://sleepercdn.com/content/nfl/players/${player.sleeper_id}.jpg`}
          alt=""
          onError={e => { e.target.style.display = 'none'; }}
          className={isCompare ? 'pc-avatar-compare' : undefined}
          style={{ width: isCompare ? 48 : 72, height: isCompare ? 48 : 72, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, background: '#0d1320', border: `2px solid ${posColor.bg}` }}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <h2 className={isCompare ? 'pc-name-compare' : undefined} style={{ fontFamily: "'Outfit', sans-serif", fontSize: isCompare ? 15 : 20, fontWeight: 700, color: '#fff', margin: 0, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <span className="pc-name-full">{player.name}</span>
              <span className="pc-name-abbr">{abbreviatedName}</span>
            </h2>
            {/* 🔴 Inside PlayerColumn, so compare mode gets one per column tied
                to THAT column's player — the row already renders a column per
                name, so there is no shared-state to keep straight. It reads the
                context itself rather than taking props, because the three
                PlayerColumn call sites (single, compare desktop, compare
                mobile) are exactly the kind of list a new prop gets forgotten
                on — `activeLeagueName` is already missing from two of them. */}
            <CardStarToggle player={player} isCompare={isCompare} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
            <span className="tag" style={{ background: posColor.bg, color: posColor.text, fontSize: 10 }}>{player.pos}</span>
            <span style={{ color: '#94a3b8', fontSize: isCompare ? 11 : 13, fontWeight: 600 }}>{player.team}</span>
            {player.age && <span style={{ color: '#64748b', fontSize: isCompare ? 10 : 12 }}>Age {player.age}</span>}
            {!isCompare && BYE_WEEKS_2026[player.team] && <span style={{ color: '#64748b', fontSize: 12 }}>Bye {BYE_WEEKS_2026[player.team]}</span>}
          </div>
        </div>
      </div>

      {/* Value Row */}
      <div className={`pc-value-row${isCompare ? ' pc-value-row-compact' : ''}`}>
        <div className="pc-value-cell">
          <span className="pc-value-label">TIER</span>
          <span className="tag" style={{ background: `${tierColor}22`, color: tierColor, border: `1px solid ${tierColor}44`, fontSize: 11, padding: '2px 10px' }}>
            {tierNum != null ? `T${tierNum}` : '—'}
          </span>
          {!isCompare && <span style={{ color: '#64748b', fontSize: 10, marginTop: 2 }}>{tierLabel}</span>}
        </div>
        <div className="pc-value-cell">
          <span className="pc-value-label">PRICE</span>
          <span className="pc-value-num" style={{ color: '#fff', fontSize: isCompare ? 15 : 18, fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>${price}</span>
        </div>
        <div className="pc-value-cell">
          <span className="pc-value-label">ADP</span>
          <span className="pc-value-num" style={{ color: '#e2e8f0', fontSize: isCompare ? 15 : 18, fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>{adpDisplay}</span>
        </div>
        <div
          className="pc-value-cell"
          onClick={() => setShowVorpExplain(v => !v)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowVorpExplain(v => !v); } }}
          role="button"
          tabIndex={0}
          title="Tap to explain VORP/G"
          style={{ cursor: 'pointer' }}
        >
          <span className="pc-value-label">VORP/G</span>
          <span className="pc-value-num" style={{ color: vpgColor, fontSize: isCompare ? 15 : 18, fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>
            {vpgSigned}/G
          </span>
        </div>
        {/* 🔴 "PROJ PPG", NOT "'26 PROJ PPG". Measured in headless Chrome against
            the shipped CSS at the card's real width: six cells in a 500px panel
            gives each label 65.3px, and "'26 PROJ PPG" renders 65.3px wide and
            wraps to two lines, which pushes this tile taller than the five
            beside it. "PROJ PPG" comes in at 54px on one line. The season is
            carried by the title and by the '25 tile next door. */}
        <div className="pc-value-cell" title="Mock1's 2026 projected points per game">
          <span className="pc-value-label">PROJ PPG</span>
          <span className="pc-value-num" style={{ color: '#e2e8f0', fontSize: isCompare ? 15 : 18, fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>{projectedPPG != null ? projectedPPG.toFixed(1) : '—'}</span>
        </div>
        <div className="pc-value-cell">
          <span className="pc-value-label">'25 PPG</span>
          <span className="pc-value-num" style={{ color: '#e2e8f0', fontSize: isCompare ? 15 : 18, fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>{actualPPG != null ? actualPPG : '—'}</span>
        </div>
      </div>
      {showVorpExplain && (
        <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', marginTop: 6, lineHeight: 1.3 }}>
          About {vpgSigned} more fantasy points per week than a replaceable starter — bigger = a bigger weekly edge.
        </div>
      )}
      {!isCompare && (
        <div style={{ fontSize: 10, color: '#64748b', textAlign: 'center', marginTop: 6, lineHeight: 1.3 }}>
          {scoringNote}
        </div>
      )}

      {/* Compare search slot — renders between value row and chart in single mode */}
      {compareSearch}

      {/* Weekly Chart — individual only in single mode */}
      {showWeeklyViz && (
        <WeeklyChart
          gsis_id={player.gsis_id} pos={player.pos} bye={BYE_WEEKS_2026[player.team]}
          scoring={scoring} selectedSeason={selectedSeason} setSelectedSeason={setSelectedSeason} posColor={posColor}
        />
      )}
      {showWeeklyViz && careerStats && (
        <div style={{ marginTop: 8, fontSize: 11, color: '#64748b', textAlign: 'center' }}>
          Career: typical <span style={{ color: '#94a3b8', fontWeight: 700 }}>{careerStats.median.toFixed(1)}</span> PPG · range {careerStats.min.toFixed(1)}–{careerStats.max.toFixed(1)} over {careerStats.n} games
        </div>
      )}

      {/* Career Stats — individual table only in single mode */}
      {!isCompare && ['QB', 'RB', 'WR', 'TE'].includes(player.pos) && (
        <CareerStats playerName={player.name} pos={player.pos} scoring={scoring} gsis_id={player.gsis_id} />
      )}

      {/* Coaching Staff — hidden in compare mode */}
      {!isCompare && coaching2026 && ['QB', 'RB', 'WR', 'TE'].includes(player.pos) && (
        <div style={{ marginTop: 20 }}>
          <div className="pc-section-label">COACHING STAFF</div>
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <div className="pc-coach-card">
              <span style={{ color: '#64748b', fontSize: 10, fontWeight: 600, letterSpacing: 0.5 }}>HC</span>
              <span style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600 }}>{coaching2026.hc}</span>
              <span style={{ color: '#475569', fontSize: 10 }}>Yr {coaching2026.hcYear}</span>
            </div>
            <div className="pc-coach-card">
              <span style={{ color: '#64748b', fontSize: 10, fontWeight: 600, letterSpacing: 0.5 }}>OC</span>
              <span style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600 }}>{coaching2026.oc}</span>
              <span style={{ color: '#475569', fontSize: 10 }}>Yr {coaching2026.ocYear}{coaching2026.playCaller === 'OC' ? ' · Play caller' : ''}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Combined Weekly Comparison Chart ──────────────────────────────────── */

const COMPARE_COLORS = [
  { fill: 'rgba(34,197,94,0.7)', stroke: '#22c55e', label: '#22c55e' },
  { fill: 'rgba(236,72,153,0.45)', stroke: 'rgba(236,72,153,0.9)', label: '#ec4899' },
  { fill: 'rgba(251,146,60,0.45)', stroke: 'rgba(251,146,60,0.9)', label: '#fb923c' },
];

function CompareWeeklyChart({ playerNames, scoring, selectedSeason, setSelectedSeason }) {
  const players = useMemo(() => playerNames.map(n => findPlayer(n)).filter(Boolean), [playerNames]);

  const pos = players[0]?.pos;
  const benchmark = POS_BENCHMARKS[pos] || 12;

  // Gather weekly data per player
  const allWeekly = useMemo(() => players.map(p => {
    if (!p.gsis_id) return null;
    const pw = WEEKLY_STATS[p.gsis_id];
    const raw = pw?.[selectedSeason];
    if (!raw) return null;
    return raw.map(entry => weekPts(entry, scoring, p.pos));
  }), [players, selectedSeason, scoring]);

  // Available seasons: union of all players' seasons
  const availableSeasons = useMemo(() => {
    const set = new Set();
    for (const p of players) {
      if (!p.gsis_id) continue;
      const pw = WEEKLY_STATS[p.gsis_id];
      if (pw) SEASONS.forEach(s => { if (pw[s]) set.add(s); });
    }
    return SEASONS.filter(s => set.has(s));
  }, [players]);

  const numWeeks = useMemo(() => {
    for (const w of allWeekly) { if (w) return w.length; }
    return 18;
  }, [allWeekly]);

  // Compute global max across all players for consistent y-axis
  const maxPts = useMemo(() => {
    let m = 35;
    for (const w of allWeekly) {
      if (!w) continue;
      for (const v of w) { if (v != null && v > m) m = v; }
    }
    return m;
  }, [allWeekly]);

  const W = 500, H = 140, PAD_L = 28, PAD_R = 4, PAD_T = 8, PAD_B = 20;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;
  const slotW = chartW / numWeeks;
  const barW = Math.max(4, Math.floor(slotW) - 3);
  const scaleY = (v) => PAD_T + chartH - (v / maxPts) * chartH;
  const benchmarkY = scaleY(benchmark);

  // Sort render order: tallest bar first (renders behind) for each week
  const renderOrder = useMemo(() => {
    const indices = players.map((_, i) => i);
    return Array.from({ length: numWeeks }, (_, wi) => {
      return [...indices].sort((a, b) => {
        const va = allWeekly[a]?.[wi] ?? -1;
        const vb = allWeekly[b]?.[wi] ?? -1;
        return vb - va; // tallest first
      });
    });
  }, [allWeekly, numWeeks, players.length]);

  return (
    <div style={{ padding: '0 16px 8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
        <span className="pc-section-label">WEEKLY SCORING</span>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {availableSeasons.map(s => (
            <button key={s} onClick={() => setSelectedSeason(s)} style={{
              background: selectedSeason === s ? '#22c55e' : 'rgba(255,255,255,0.05)',
              color: selectedSeason === s ? '#fff' : '#64748b',
              border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 11,
              fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}>{s}</button>
          ))}
        </div>
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
        {/* Y-axis labels */}
        {[0, Math.round(maxPts / 2), Math.round(maxPts)].map(v => (
          <text key={v} x={PAD_L - 4} y={scaleY(v) + 3} textAnchor="end" fill="#475569" fontSize="9" fontFamily="Figtree, sans-serif">{v}</text>
        ))}
        {/* Benchmark line */}
        <line x1={PAD_L} y1={benchmarkY} x2={W - PAD_R} y2={benchmarkY} stroke="#f59e0b" strokeWidth="1" strokeDasharray="4 3" opacity="0.4" />
        <text x={W - PAD_R} y={benchmarkY - 3} textAnchor="end" fill="#f59e0b" fontSize="8" fontFamily="Figtree, sans-serif" opacity="0.6">
          {pos}12
        </text>
        {/* Bars — render tallest first so shorter bars overlay on top */}
        {Array.from({ length: numWeeks }, (_, wi) => (
          <g key={wi}>
            {renderOrder[wi].map(pi => {
              const pts = allWeekly[pi]?.[wi];
              if (pts == null) return null;
              const cc = COMPARE_COLORS[pi] || COMPARE_COLORS[0];
              const barH = Math.max(1, (pts / maxPts) * chartH);
              const x = PAD_L + wi * slotW + (slotW - barW) / 2;
              const y = PAD_T + chartH - barH;
              return (
                <rect key={pi} x={x} y={y} width={barW} height={barH} rx={2}
                  fill={cc.fill} stroke={pi > 0 ? cc.stroke : 'none'} strokeWidth={pi > 0 ? 1 : 0} />
              );
            })}
          </g>
        ))}
        {/* Week labels */}
        {Array.from({ length: numWeeks }, (_, i) => (
          <text key={i} x={PAD_L + i * slotW + slotW / 2} y={H - 4} textAnchor="middle" fill="#475569" fontSize="7" fontFamily="Figtree, sans-serif">
            {i + 1}
          </text>
        ))}
      </svg>
    </div>
  );
}

/* ── Merged Comparison Table ───────────────────────────────────────────── */

const COMPARE_STATS = {
  QB: [
    { key: '_age', label: 'Age', invert: true, noPct: true },
    { key: 'gms', label: 'Games' },
    { key: 'passYds', label: 'Pass Yds', fmt: v => v?.toLocaleString() },
    { key: 'passTD', label: 'Pass TD' },
    { key: 'int', label: 'INT', invert: true },
    { key: 'rushYds', label: 'Rush Yds', fmt: v => v?.toLocaleString() },
    { key: 'rushTD', label: 'Rush TD' },
    { key: '_fpts', label: 'FPts' },
  ],
  RB: [
    { key: '_age', label: 'Age', invert: true, noPct: true },
    { key: 'gms', label: 'Games' },
    { key: 'rushAtt', label: 'Attempts' },
    { key: 'rushYds', label: 'Rush Yds', fmt: v => v?.toLocaleString() },
    { key: 'rushTD', label: 'Rush TD' },
    { key: 'targets', label: 'Targets' },
    { key: 'rec', label: 'Receptions' },
    { key: 'recYds', label: 'Rec Yds', fmt: v => v?.toLocaleString() },
    { key: 'recTD', label: 'Rec TD' },
    { key: '_fpts', label: 'FPts' },
  ],
  WR: [
    { key: '_age', label: 'Age', invert: true, noPct: true },
    { key: 'gms', label: 'Games' },
    { key: 'targets', label: 'Targets' },
    { key: 'rec', label: 'Receptions' },
    { key: 'recYds', label: 'Rec Yds', fmt: v => v?.toLocaleString() },
    { key: 'recTD', label: 'Rec TD' },
    { key: 'rushAtt', label: 'Rush Att' },
    { key: 'rushYds', label: 'Rush Yds', fmt: v => v?.toLocaleString() },
    { key: '_fpts', label: 'FPts' },
  ],
  TE: [
    { key: '_age', label: 'Age', invert: true, noPct: true },
    { key: 'gms', label: 'Games' },
    { key: 'targets', label: 'Targets' },
    { key: 'rec', label: 'Receptions' },
    { key: 'recYds', label: 'Rec Yds', fmt: v => v?.toLocaleString() },
    { key: 'recTD', label: 'Rec TD' },
    { key: '_fpts', label: 'FPts' },
  ],
};

function buildPercentileMap(pos, season, statKeys) {
  const allRows = [];
  for (const seasons of Object.values(PLAYER_HISTORY)) {
    const row = seasons.find(r => r.pos === pos && String(r.year) === String(season));
    if (row) allRows.push(row);
  }
  const map = {};
  for (const key of statKeys) {
    if (key === '_fpts') continue;
    const vals = allRows.map(r => r[key] ?? 0).sort((a, b) => a - b);
    map[key] = vals;
  }
  return map;
}

function getPercentile(sortedVals, value) {
  if (!sortedVals || sortedVals.length === 0) return 0;
  let count = 0;
  for (const v of sortedVals) { if (v < value) count++; }
  return Math.round((count / sortedVals.length) * 100);
}

function CompareTable({ playerNames, scoring, selectedSeason }) {
  const [perGame, setPerGame] = useState(false);
  const players = useMemo(() => playerNames.map(n => findPlayer(n)).filter(Boolean), [playerNames]);

  const pos = players.length >= 2 ? players[0].pos : null;
  const statDefs = pos ? COMPARE_STATS[pos] : null;

  const season = Number(selectedSeason);

  // Compute all derived data unconditionally (hooks must not be behind early returns)
  const { rows, fpts } = useMemo(() => {
    if (!statDefs || players.length < 2) return { rows: [], fpts: [] };

    const r = players.map(p => {
      const h = findPlayerHist(p.name);
      return h?.find(row => row.year === season) || null;
    });

    const f = r.map((row, i) => {
      if (!row || !scoring) return null;
      const gsis = players[i].gsis_id;
      const weeklyData = gsis ? WEEKLY_STATS[gsis] : null;
      const bonusGames = countBonusGames(weeklyData, String(row.year ?? season), scoring);
      return computeFPts(row, pos, scoring, bonusGames);
    });

    return { rows: r, fpts: f };
  }, [players, statDefs, pos, scoring, season]);

  const statKeys = statDefs ? statDefs.map(s => s.key) : [];
  const pctMap = useMemo(() => {
    if (!pos || !statKeys.length) return {};
    return buildPercentileMap(pos, season, statKeys);
  }, [pos, season, statKeys.join(',')]);

  if (players.length < 2 || !statDefs) return null;

  return (
    <div style={{ padding: '0 16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <span className="pc-section-label">STAT COMPARISON</span>
        <span style={{ fontSize: 10, color: '#475569' }}>{season}</span>
        <div style={{ display: 'flex', marginLeft: 'auto', borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
          {['Totals', 'Per Game'].map(mode => {
            const active = mode === 'Totals' ? !perGame : perGame;
            return (
              <button key={mode} onClick={() => setPerGame(mode === 'Per Game')} style={{
                padding: '3px 10px', fontSize: 10, fontWeight: active ? 700 : 500, fontFamily: 'inherit',
                background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
                color: active ? '#e2e8f0' : '#64748b',
                border: 'none', cursor: 'pointer',
              }}>{mode}</button>
            );
          })}
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: "'Figtree', sans-serif" }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', color: '#64748b', fontSize: 10, fontWeight: 600, padding: '6px 8px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>Stat</th>
            {players.map(p => {
              const pc = POS_COLORS[p.pos] || POS_COLORS.BN;
              return (
                <th key={p.name} style={{ textAlign: 'center', color: '#e2e8f0', fontSize: 11, fontWeight: 700, padding: '6px 8px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <span style={{ color: pc.bg, marginRight: 4, fontSize: 9 }}>{p.pos}</span>
                  {getLastName(p.name)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {statDefs.map(stat => {
            const isGames = stat.key === 'gms';
            const isAge = stat.key === '_age';
            const rawValues = rows.map((row, i) => {
              if (isAge) return players[i]?.age ?? null;
              if (stat.key === '_fpts') return fpts[i];
              return row?.[stat.key] ?? null;
            });
            const displayValues = rawValues.map((val, i) => {
              if (val == null || isGames || isAge || !perGame) return val;
              const gms = rows[i]?.gms;
              return gms > 0 ? val / gms : 0;
            });
            const numValues = displayValues.filter(v => v != null).map(Number);
            const maxVal = stat.invert ? Math.min(...numValues) : Math.max(...numValues);
            const hasWinner = numValues.length > 1 && new Set(numValues.map(v => v.toFixed(1))).size > 1;

            return (
              <tr key={stat.key}>
                <td style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, padding: '8px 8px 10px', borderBottom: '1px solid rgba(255,255,255,0.04)', whiteSpace: 'nowrap' }}>
                  {stat.label}
                </td>
                {displayValues.map((val, i) => {
                  const isWinner = hasWinner && val != null && Number(val) === maxVal;
                  const formatted = val == null ? '—'
                    : (perGame && !isGames && !isAge) ? Number(val).toFixed(1)
                    : (stat.fmt ? stat.fmt(val) : val);
                  const showPct = !stat.noPct && stat.key !== '_fpts' && rawValues[i] != null;
                  const pct = showPct ? getPercentile(pctMap[stat.key], Number(rawValues[i])) : null;
                  const pc = POS_COLORS[players[i]?.pos] || POS_COLORS.BN;
                  return (
                    <td key={i} style={{
                      textAlign: 'center', padding: '8px 8px 10px',
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      background: isWinner ? 'rgba(34,197,94,0.08)' : 'transparent',
                    }}>
                      <span style={{ color: isWinner ? '#22c55e' : '#e2e8f0', fontWeight: isWinner ? 700 : 500, fontSize: 13 }}>
                        {formatted}
                      </span>
                      {pct != null && (
                        <div style={{ marginTop: 4, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: pc.bg, opacity: 0.7 }} />
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── Unified Compare Legend ────────────────────────────────────────────── */

function UnifiedCompareLegend({ playerNames, scoring, selectedSeason }) {
  const rows = useMemo(() => {
    const isCareer = selectedSeason === 'Career';
    return playerNames.map((n, i) => {
      const p = findPlayer(n);
      if (!p) return null;
      const pw = p.gsis_id ? WEEKLY_STATS[p.gsis_id] : null;
      const seasons = isCareer ? SEASONS : [selectedSeason];
      const pts = [];
      if (pw) {
        for (const s of seasons) {
          const weeks = pw[s];
          if (!weeks) continue;
          for (const entry of weeks) {
            const v = weekPts(entry, scoring, p.pos);
            if (typeof v === 'number') pts.push(v);
          }
        }
      }
      const cc = COMPARE_COLORS[i] || COMPARE_COLORS[0];
      const stripped = stripSuffix(p.name);
      const parts = stripped.split(/\s+/).filter(Boolean);
      const abbrev = parts.length >= 2 ? `${parts[0][0]}. ${parts[parts.length - 1]}` : stripped;
      const avg = pts.length ? pts.reduce((s, v) => s + v, 0) / pts.length : null;
      const min = pts.length ? Math.min(...pts) : null;
      const max = pts.length ? Math.max(...pts) : null;
      return { name: abbrev, color: cc.label, avg, min, max };
    }).filter(Boolean);
  }, [playerNames, scoring, selectedSeason]);

  if (rows.length === 0) return null;

  return (
    <div className="pc-compare-legend">
      {rows.map((r, i) => (
        <div key={i} className="pc-compare-legend-row">
          <span className="pc-legend-dot" style={{ background: r.color }} />
          <span className="pc-legend-name">{r.name}</span>
          {r.avg != null && (
            <span className="pc-legend-avg">{r.avg.toFixed(1)}/G</span>
          )}
          {r.min != null && r.max != null && (
            <span className="pc-legend-range">· range {r.min.toFixed(1)}–{r.max.toFixed(1)}</span>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Compare Search ────────────────────────────────────────────────────── */

function CompareSearch({ onSelect, existingNames, primaryName, label = '+ Compare' }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const results = useMemo(() => {
    if (!query || query.length < 2) return [];
    const q = query.toLowerCase();
    return UNIQUE_PLAYERS
      .filter(p => p.name.toLowerCase().includes(q) && !existingNames.includes(p.name))
      .slice(0, 8);
  }, [query, existingNames]);

  // Tier-mate quick picks
  const tierMates = useMemo(() => {
    if (!primaryName) return [];
    const primary = findPlayer(primaryName);
    if (!primary) return [];
    const rn = parseRankNum(getEffectivePosRank(primary.name, primary.posRank) || primary.posRank);
    const tierObj = getTierForRank(primary.pos, rn);
    if (!tierObj) return [];
    return UNIQUE_PLAYERS.filter(p => {
      if (p.pos !== primary.pos || existingNames.includes(p.name)) return false;
      const prn = parseRankNum(getEffectivePosRank(p.name, p.posRank) || p.posRank);
      return prn >= tierObj.min && prn <= tierObj.max;
    }).slice(0, 6);
  }, [primaryName, existingNames]);

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px',
        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20,
        color: '#94a3b8', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
      }}>{label}</button>
    );
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', flex: 1 }}>
      {/* Tier-mate quick picks */}
      {tierMates.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#64748b', letterSpacing: '1px', marginBottom: 5 }}>SAME TIER</div>
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
            {tierMates.map(p => {
              const pc = POS_COLORS[p.pos] || POS_COLORS.BN;
              return (
                <button key={p.name} onClick={() => { onSelect(p.name); setQuery(''); setOpen(false); }} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px',
                  background: 'rgba(255,255,255,0.04)', border: `1px solid ${pc.bg}33`,
                  borderRadius: 14, fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                  color: '#e2e8f0', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                  transition: 'background 0.1s ease',
                }}
                onMouseEnter={e => e.currentTarget.style.background = `${pc.bg}18`}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                >
                  {getLastName(p.name)} <span style={{ color: '#64748b', fontSize: 10 }}>{p.team}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <input
        autoFocus
        type="text"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); setQuery(''); } }}
        placeholder="Search player to compare..."
        style={{
          width: '100%', padding: '7px 12px', fontSize: 12, fontFamily: 'inherit',
          background: '#0d1320', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 20,
          color: '#fff', outline: 'none', boxSizing: 'border-box',
        }}
      />
      {results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10,
          marginTop: 4, maxHeight: 280, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
        }}>
          {results.map((p, i) => {
            const pc = POS_COLORS[p.pos] || POS_COLORS.BN;
            const isLast = i === results.length - 1;
            return (
              <div key={p.name} onClick={() => { onSelect(p.name); setQuery(''); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', cursor: 'pointer',
                  borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.04)',
                  transition: 'background 0.1s ease',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span className="tag" style={{ background: pc.bg, color: pc.text, fontSize: 9, padding: '2px 7px', flexShrink: 0 }}>{p.pos}</span>
                <span style={{ color: '#e2e8f0', fontSize: 12, fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                <span style={{ color: '#64748b', fontSize: 11, flexShrink: 0 }}>{p.team}</span>
              </div>
            );
          })}
        </div>
      )}
      {query.length >= 2 && results.length === 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10,
          marginTop: 4, padding: '10px 12px', color: '#64748b', fontSize: 11,
        }}>No matches</div>
      )}
    </div>
  );
}

/* ── Main PlayerCard ───────────────────────────────────────────────────── */

export default function PlayerCard() {
  const { playerNames, isCompareMode, closePlayerCard, addComparePlayer, removePlayer, scoring, dynamicPrices, budget, activeLeagueName } = usePlayerCard();
  const [compareSeason, setCompareSeason] = useState('2025');
  const isOpen = playerNames.length > 0;
  const count = playerNames.length;
  const replacementLevels = useMemo(() => computeReplacementLevels(scoring), [scoring]);

  if (!isOpen) {
    return (
      <>
        <div className="player-card-backdrop" />
        <div className="player-card-panel" />
      </>
    );
  }

  const panelWidth = count <= 1 ? 500 : count === 2 ? 750 : 1050;

  return (
    <>
      <div className="player-card-backdrop open" onClick={closePlayerCard} />
      <div className="player-card-panel open" style={{ width: panelWidth }}>
        <button className="player-card-close" onClick={closePlayerCard} aria-label="Close">&times;</button>

        {/* Desktop: side-by-side columns — hidden on mobile via CSS when comparing */}
        <div className="pc-columns-desktop" style={{ display: 'flex' }}>
          {isCompareMode ? (
            playerNames.map((name, i) => {
              const isLast = i === playerNames.length - 1;
              const compareSearchSlot = (isLast && count < 3) ? (
                <div style={{ marginTop: 12, marginBottom: 12 }}>
                  <CompareSearch onSelect={addComparePlayer} existingNames={playerNames} primaryName={playerNames[0]} label="+ Add another" />
                </div>
              ) : undefined;
              return (
                <div key={name} style={{ flex: 1, minWidth: 0, borderRight: '1px solid rgba(255,255,255,0.06)' }}>
                  <PlayerColumn name={name} scoring={scoring} replacementLevels={replacementLevels} dynamicPrices={dynamicPrices} budget={budget} isCompare onRemove={() => removePlayer(name)} compareSearch={compareSearchSlot} />
                </div>
              );
            })
          ) : (
            <div style={{ flex: 1 }}>
              <PlayerColumn name={playerNames[0]} scoring={scoring} replacementLevels={replacementLevels} dynamicPrices={dynamicPrices} budget={budget} activeLeagueName={activeLeagueName} isCompare={false}
                compareSearch={count < 3 ? <div style={{ marginTop: 12, marginBottom: 12 }}><CompareSearch onSelect={addComparePlayer} existingNames={playerNames} primaryName={playerNames[0]} /></div> : undefined}
              />
            </div>
          )}
        </div>

        {/* Mobile: all players side-by-side as compact cards — hidden on desktop via CSS */}
        {isCompareMode && (
          <div className="pc-column-mobile">
            <div className="pc-mobile-compare-row" style={{ '--pc-mobile-cards': playerNames.length }}>
              {playerNames.map((name, i) => {
                const isLast = i === playerNames.length - 1;
                const compareSearchSlot = (isLast && count < 3) ? (
                  <div style={{ marginTop: 12, marginBottom: 12 }}>
                    <CompareSearch onSelect={addComparePlayer} existingNames={playerNames} primaryName={playerNames[0]} label="+ Add another" />
                  </div>
                ) : undefined;
                return (
                  <PlayerColumn
                    key={name}
                    name={name}
                    scoring={scoring}
                    replacementLevels={replacementLevels}
                    isCompare
                    onRemove={() => removePlayer(name)}
                    compareSearch={compareSearchSlot}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Unified legend — shared between weekly chart and range of outcomes */}
        {isCompareMode && <UnifiedCompareLegend playerNames={playerNames} scoring={scoring} selectedSeason={compareSeason} />}

        {/* Combined weekly chart — full width in compare mode */}
        {isCompareMode && <CompareWeeklyChart playerNames={playerNames} scoring={scoring} selectedSeason={compareSeason} setSelectedSeason={setCompareSeason} />}

        {/* Merged comparison table — full width below columns */}
        {isCompareMode && <CompareTable playerNames={playerNames} scoring={scoring} selectedSeason={compareSeason} />}
      </div>
    </>
  );
}

/* ── Weekly Scoring Bar Chart (inline SVG) ──────────────────────────────── */

// Compute fantasy points from a weekly stat entry using league scoring, returning
// the total AND its itemized line items ({label, pts, bonus}). weekPts() below
// delegates here, so any breakdown built from `items` sums to the bar/tile value
// by construction. `add` skips zero terms, so only non-zero lines are itemized.
// entry can be a plain number (legacy) or an object {fp, passYds, passTD, int, rushYds, rushTD, rec, recYds, recTD, fum, fumLost}.
function weekPtsDetailed(entry, scoring, pos) {
  if (entry == null) return null;
  if (typeof entry === 'number') return { total: entry, items: [] };
  if (!scoring) return { total: entry.fp ?? 0, items: [] };
  let total = 0;
  const items = [];
  const add = (p, label, bonus) => { if (p) { total += p; items.push({ label, pts: p, bonus: !!bonus }); } };

  add(entry.passYds ? entry.passYds / scoring.passYdsPer : 0, `Pass ${entry.passYds} yds`);
  add(entry.passTD ? entry.passTD * scoring.passTD : 0, `Pass TD ×${entry.passTD}`);
  add(entry.int ? entry.int * scoring.passInt : 0, `INT ×${entry.int}`);
  add(entry.rushYds ? entry.rushYds / scoring.rushYdsPer : 0, `Rush ${entry.rushYds} yds`);
  add(entry.rushTD ? entry.rushTD * scoring.rushTD : 0, `Rush TD ×${entry.rushTD}`);
  add(entry.rec ? entry.rec * scoring.receptions : 0, `Rec ×${entry.rec}`);
  // Position reception premium (e.g. FFPC TE +0.5/rec) — mirrors calculateProjection.
  const recBonus = pos === 'RB' ? scoring.recBonusRB : pos === 'WR' ? scoring.recBonusWR : pos === 'TE' ? scoring.recBonusTE : 0;
  add(entry.rec && recBonus ? entry.rec * recBonus : 0, `${pos} premium ×${entry.rec} rec`, true);
  add(entry.recYds ? entry.recYds / scoring.recYdsPer : 0, `Rec ${entry.recYds} yds`);
  add(entry.recTD ? entry.recTD * scoring.recTD : 0, `Rec TD ×${entry.recTD}`);
  // Yardage bonuses (per-game threshold)
  if (scoring.passYdsBonusThreshold > 0 && scoring.passYdsBonus > 0 && entry.passYds >= scoring.passYdsBonusThreshold) add(scoring.passYdsBonus, `${scoring.passYdsBonusThreshold}-yd pass bonus`, true);
  if (scoring.rushYdsBonusThreshold > 0 && scoring.rushYdsBonus > 0 && entry.rushYds >= scoring.rushYdsBonusThreshold) add(scoring.rushYdsBonus, `${scoring.rushYdsBonusThreshold}-yd rush bonus`, true);
  if (scoring.recYdsBonusThreshold > 0 && scoring.recYdsBonus > 0 && entry.recYds >= scoring.recYdsBonusThreshold) add(scoring.recYdsBonus, `${scoring.recYdsBonusThreshold}-yd rec bonus`, true);
  // Fumbles — merged per-game from nflverse weekly (fum/fumLost). Applied EXACTLY
  // as calculateProjection does so per-game PPG matches the season projection.
  // Convention B: fumble weight on NON-lost fumbles only (mirror calculateProjection).
  const fumLost = entry.fumLost || 0;
  const fumKept = Math.max(0, (entry.fum || 0) - fumLost);
  add(fumKept ? fumKept * scoring.fumbles : 0, `Fumble ×${fumKept}`);
  add(fumLost ? fumLost * scoring.fumblesLost : 0, `Fumble lost ×${fumLost}`);
  return { total, items };
}

// Total-only helper (the common case). Delegates to weekPtsDetailed so the two
// can never drift apart.
function weekPts(entry, scoring, pos) {
  const d = weekPtsDetailed(entry, scoring, pos);
  return d == null ? null : d.total;
}

function WeeklyChart({ gsis_id, pos, bye, scoring, selectedSeason, setSelectedSeason, posColor }) {
  const playerWeekly = WEEKLY_STATS[gsis_id];
  const benchmark = POS_BENCHMARKS[pos] || 12;

  // Season tabs: the years this player has data for (SEASONS is ordered most-recent first).
  const availableSeasons = useMemo(() => {
    if (!playerWeekly) return [];
    return SEASONS.filter(s => playerWeekly[s]);
  }, [playerWeekly]);

  // Selected year, falling back to the most-recent available if the default has no data.
  const displaySeason = availableSeasons.includes(selectedSeason) ? selectedSeason : availableSeasons[0];
  const rawWeeks = playerWeekly && displaySeason ? playerWeekly[displaySeason] : null;

  // Convert raw entries to scored points (handles both number and object formats)
  const scoredWeeks = useMemo(() => {
    if (!rawWeeks) return null;
    return rawWeeks.map(entry => weekPts(entry, scoring, pos));
  }, [rawWeeks, scoring, pos]);

  // PPR/G average always reflects what the chart shows (most-recent in Career, selected year otherwise)
  const nonNull = scoredWeeks ? scoredWeeks.filter(v => v != null) : [];
  const avg = nonNull.length > 0 ? nonNull.reduce((s, v) => s + v, 0) / nonNull.length : 0;

  // Weekly-bar breakdown popover: hover a bar (mouse) or tap it (touch). The
  // itemized lines come from weekPtsDetailed — the SAME pass that scores the bar.
  const [activeBar, setActiveBar] = useState(null);
  const lastPointer = useRef('mouse');
  const chartWrapRef = useRef(null);
  useEffect(() => {
    if (activeBar == null) return;
    const onDocPointer = (e) => {
      if (chartWrapRef.current && !chartWrapRef.current.contains(e.target)) setActiveBar(null);
    };
    document.addEventListener('pointerdown', onDocPointer);
    return () => document.removeEventListener('pointerdown', onDocPointer);
  }, [activeBar]);
  const activeDetail = (activeBar != null && rawWeeks && rawWeeks[activeBar] != null)
    ? weekPtsDetailed(rawWeeks[activeBar], scoring, pos) : null;

  // Chart dimensions
  const W = 352, H = 120, PAD_L = 28, PAD_R = 4, PAD_T = 8, PAD_B = 20;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;
  const numWeeks = scoredWeeks ? scoredWeeks.length : 17;
  const barW = Math.floor(chartW / numWeeks) - 2;
  const maxPts = scoredWeeks ? Math.max(35, ...scoredWeeks.filter(v => v != null)) : 35;

  const scaleY = (v) => PAD_T + chartH - (v / maxPts) * chartH;
  const benchmarkY = scaleY(benchmark);

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
        <span className="pc-section-label">WEEKLY SCORING</span>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {availableSeasons.map(s => (
            <button
              key={s}
              onClick={() => setSelectedSeason(s)}
              style={{
                background: displaySeason === s ? posColor.bg : 'rgba(255,255,255,0.05)',
                color: displaySeason === s ? '#fff' : '#64748b',
                border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 11,
                fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >{s}</button>
          ))}
        </div>
      </div>
      {scoredWeeks ? (
        <>
          <div ref={chartWrapRef} style={{ position: 'relative' }}>
          <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ marginTop: 8, display: 'block' }}>
            {/* Y-axis labels */}
            {[0, Math.round(maxPts / 2), Math.round(maxPts)].map(v => (
              <text key={v} x={PAD_L - 4} y={scaleY(v) + 3} textAnchor="end" fill="#475569" fontSize="9" fontFamily="Figtree, sans-serif">{v}</text>
            ))}
            {/* Benchmark line */}
            <line x1={PAD_L} y1={benchmarkY} x2={W - PAD_R} y2={benchmarkY} stroke="#f59e0b" strokeWidth="1" strokeDasharray="4 3" opacity="0.6" />
            <text x={W - PAD_R} y={benchmarkY - 3} textAnchor="end" fill="#f59e0b" fontSize="8" fontFamily="Figtree, sans-serif" opacity="0.8">
              {pos}12
            </text>
            {/* Bars + null-week markers */}
            {scoredWeeks.map((pts, i) => {
              const weekNum = i + 1;
              const x = PAD_L + i * (chartW / numWeeks) + 1;
              const cx = x + barW / 2;

              if (pts == null) {
                const isBye = weekNum === bye;
                const markerY = PAD_T + chartH - 2;
                if (isBye) {
                  return (
                    <text key={i} x={cx} y={markerY} textAnchor="middle" fill="#475569" fontSize="8" fontWeight="700" fontFamily="Figtree, sans-serif">B</text>
                  );
                }
                return (
                  <text key={i} x={cx} y={markerY} textAnchor="middle" fill="#ef4444" fontSize="9" fontWeight="700" fontFamily="Figtree, sans-serif">&times;</text>
                );
              }

              const barH = Math.max(1, (pts / maxPts) * chartH);
              const y = PAD_T + chartH - barH;
              const aboveBench = pts >= benchmark;
              return (
                <g key={i}>
                  <rect
                    x={x} y={y} width={barW} height={barH} rx={2}
                    fill={aboveBench ? posColor.bg : `${posColor.bg}55`}
                  />
                  {pts >= 20 && (
                    <text x={cx} y={y - 2} textAnchor="middle" fill="#94a3b8" fontSize="7" fontFamily="Figtree, sans-serif">
                      {Math.round(pts)}
                    </text>
                  )}
                </g>
              );
            })}
            {/* Week labels */}
            {Array.from({ length: numWeeks }, (_, i) => (
              <text key={i} x={PAD_L + i * (chartW / numWeeks) + 1 + barW / 2} y={H - 4} textAnchor="middle" fill="#475569" fontSize="7" fontFamily="Figtree, sans-serif">
                {i + 1}
              </text>
            ))}
            {/* Transparent full-height hit areas (per played week) + active highlight */}
            {scoredWeeks.map((pts, i) => {
              if (pts == null) return null;
              const colX = PAD_L + i * (chartW / numWeeks);
              const colW = chartW / numWeeks;
              return (
                <g key={`hit-${i}`}>
                  {activeBar === i && <rect x={colX} y={PAD_T} width={colW} height={chartH} fill="rgba(255,255,255,0.07)" rx={2} />}
                  <rect
                    x={colX} y={PAD_T} width={colW} height={chartH}
                    fill="transparent" style={{ pointerEvents: 'all', cursor: 'pointer' }}
                    onPointerDown={(e) => { lastPointer.current = e.pointerType; }}
                    onPointerEnter={(e) => { if (e.pointerType === 'mouse') setActiveBar(i); }}
                    onPointerLeave={(e) => { if (e.pointerType === 'mouse') setActiveBar(null); }}
                    onClick={() => { if (lastPointer.current !== 'mouse') setActiveBar(prev => (prev === i ? null : i)); }}
                  />
                </g>
              );
            })}
          </svg>
          {/* Breakdown popover — anchored horizontally to the active bar */}
          {activeDetail && (() => {
            const frac = (PAD_L + activeBar * (chartW / numWeeks) + (chartW / numWeeks) / 2) / W;
            const tx = frac < 0.34 ? '0%' : frac > 0.66 ? '-100%' : '-50%';
            // Round each line to 1dp, then push the ±0.1 rounding residual (from
            // e.g. passYds/20 not being a clean tenth) onto the largest-magnitude
            // line so the displayed column ALWAYS sums to the bar's shown value.
            const barVal = Number(activeDetail.total.toFixed(1));
            const disp = activeDetail.items.map(it => Number(it.pts.toFixed(1)));
            const resid = Number((barVal - disp.reduce((s, v) => s + v, 0)).toFixed(1));
            if (resid !== 0 && disp.length) {
              let big = 0;
              for (let k = 1; k < disp.length; k++) if (Math.abs(activeDetail.items[k].pts) > Math.abs(activeDetail.items[big].pts)) big = k;
              disp[big] = Number((disp[big] + resid).toFixed(1));
            }
            return (
              <div style={{
                position: 'absolute', left: `${frac * 100}%`, top: 6, transform: `translateX(${tx})`,
                zIndex: 20, background: '#0b1220', border: '1px solid #334155', borderRadius: 8,
                padding: '7px 9px', boxShadow: '0 8px 24px rgba(0,0,0,0.6)', pointerEvents: 'none',
                minWidth: 118, maxWidth: 210,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#e2e8f0', marginBottom: 5, whiteSpace: 'nowrap' }}>
                  Wk {activeBar + 1} · {barVal.toFixed(1)} pts
                </div>
                {activeDetail.items.length === 0 ? (
                  <div style={{ fontSize: 10, color: '#64748b' }}>No scoring stats</div>
                ) : activeDetail.items.map((it, k) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 10, lineHeight: 1.5, whiteSpace: 'nowrap' }}>
                    <span style={{ color: '#94a3b8' }}>{it.label}</span>
                    <span style={{ color: disp[k] < 0 ? '#f87171' : it.bonus ? '#22c55e' : '#e2e8f0', fontWeight: 600 }}>
                      {it.bonus && disp[k] >= 0 ? `+${disp[k].toFixed(1)}` : disp[k].toFixed(1)}
                    </span>
                  </div>
                ))}
              </div>
            );
          })()}
          </div>
          {/* PPR/G average */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, padding: '0 2px' }}>
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>
              Avg: <span style={{ color: avg >= benchmark ? '#22c55e' : '#e2e8f0', fontWeight: 700 }}>{avg.toFixed(1)}</span> PPR/G
            </span>
            <span style={{ fontSize: 10, color: '#475569' }}>{nonNull.length}G played</span>
          </div>
        </>
      ) : (
        <p style={{ color: '#475569', fontSize: 12, marginTop: 10 }}>No weekly data for {selectedSeason}</p>
      )}
    </div>
  );
}

/* ── Career Stats Table ─────────────────────────────────────────────────── */

// Count bonus games from weekly stat entries for a given season
function countBonusGames(weeklyData, season, scoring) {
  const weeks = weeklyData?.[season];
  if (!weeks || !scoring) return { pass: 0, rush: 0, rec: 0 };
  let pass = 0, rush = 0, rec = 0;
  for (const w of weeks) {
    if (w == null || typeof w === 'number') continue;
    if (scoring.passYdsBonusThreshold > 0 && w.passYds >= scoring.passYdsBonusThreshold) pass++;
    if (scoring.rushYdsBonusThreshold > 0 && w.rushYds >= scoring.rushYdsBonusThreshold) rush++;
    if (scoring.recYdsBonusThreshold > 0 && w.recYds >= scoring.recYdsBonusThreshold) rec++;
  }
  return { pass, rush, rec };
}

function computeFPts(row, pos, scoring, bonusGames) {
  if (!scoring) return null;
  let pts = 0;
  if (row.passYds) pts += row.passYds / scoring.passYdsPer;
  if (row.passTD) pts += row.passTD * scoring.passTD;
  if (row.int) pts += row.int * scoring.passInt;
  if (row.rushYds) pts += row.rushYds / scoring.rushYdsPer;
  if (row.rushTD) pts += row.rushTD * scoring.rushTD;
  if (row.rec) pts += row.rec * scoring.receptions;
  if (row.recYds) pts += row.recYds / scoring.recYdsPer;
  if (row.recTD) pts += row.recTD * scoring.recTD;
  // Yardage bonuses from weekly game counts
  if (bonusGames) {
    if (scoring.passYdsBonus > 0) pts += bonusGames.pass * scoring.passYdsBonus;
    if (scoring.rushYdsBonus > 0) pts += bonusGames.rush * scoring.rushYdsBonus;
    if (scoring.recYdsBonus > 0) pts += bonusGames.rec * scoring.recYdsBonus;
  }
  return Math.round(pts);
}

const CAREER_COLS = {
  QB: [
    { key: 'year', label: 'Year' },
    { key: 'gms', label: 'G' },
    { key: 'passYds', label: 'Pass Yds', fmt: v => v?.toLocaleString() },
    { key: 'passTD', label: 'TD' },
    { key: 'int', label: 'INT' },
    { key: 'rushYds', label: 'Rush Yds', fmt: v => v?.toLocaleString() },
    { key: '_fpts', label: 'FPts' },
  ],
  RB: [
    { key: 'year', label: 'Year' },
    { key: 'gms', label: 'G' },
    { key: 'rushAtt', label: 'Att' },
    { key: 'rushYds', label: 'Rush Yds', fmt: v => v?.toLocaleString() },
    { key: 'rushTD', label: 'Rush TD' },
    { key: 'targets', label: 'Tgt' },
    { key: 'rec', label: 'Rec' },
    { key: 'recYds', label: 'Rec Yds', fmt: v => v?.toLocaleString() },
    { key: 'recTD', label: 'Rec TD' },
    { key: '_fpts', label: 'FPts' },
  ],
  WR: [
    { key: 'year', label: 'Year' },
    { key: 'gms', label: 'G' },
    { key: 'targets', label: 'Tgt' },
    { key: 'rec', label: 'Rec' },
    { key: 'recYds', label: 'Rec Yds', fmt: v => v?.toLocaleString() },
    { key: 'recTD', label: 'TD' },
    { key: '_fpts', label: 'FPts' },
  ],
  TE: [
    { key: 'year', label: 'Year' },
    { key: 'gms', label: 'G' },
    { key: 'targets', label: 'Tgt' },
    { key: 'rec', label: 'Rec' },
    { key: 'recYds', label: 'Rec Yds', fmt: v => v?.toLocaleString() },
    { key: 'recTD', label: 'TD' },
    { key: '_fpts', label: 'FPts' },
  ],
};

function CareerStats({ playerName, pos, scoring, gsis_id }) {
  const history = findPlayerHist(playerName);
  const cols = CAREER_COLS[pos];
  if (!history || !cols) return null;

  const weeklyData = gsis_id ? WEEKLY_STATS[gsis_id] : null;

  // Last 3 seasons, most recent first
  const rows = history.slice(0, 3);

  return (
    <div style={{ marginTop: 20 }}>
      <div className="pc-section-label">CAREER STATS</div>
      <div style={{ overflowX: 'auto', marginTop: 8 }}>
        <table className="pc-stats-table">
          <thead>
            <tr>
              {cols.map(c => <th key={c.key}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.year}>
                {cols.map(c => {
                  if (c.key === '_fpts') {
                    const bonusGames = countBonusGames(weeklyData, String(row.year), scoring);
                    const fpts = computeFPts(row, pos, scoring, bonusGames);
                    return <td key={c.key} style={{ color: '#f59e0b', fontWeight: 700 }}>{fpts ?? '—'}</td>;
                  }
                  const val = row[c.key];
                  const display = c.fmt ? c.fmt(val) : (val ?? '—');
                  return <td key={c.key}>{display}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
